import useSWR from 'swr';

import { IDetailResponse, IPaginatedResponse } from 'utils/types';

import { buildQuery, fetcher } from './utils';

export interface IMerchant {
  id: number;
  name: string;
  images: {}[];
  state: string;
  created_at: string;
  custom_fields?: {
    name: string;
    value: any;
  }[];
  tags: {
    id: number;
    name: string;
    description: string;
  }[];
  activatable: boolean;
  deactivatable: boolean;
  is_featured: boolean;
  categories: any[];
  name_en: string;
  name_th: null | string;
  name_zh: null | string;
  description_en: null | string;
  description_th: null | string;
  description_zh: null | string;
}

export const useMerchant = (id?: number | string | null) => {
  const { data, error, isLoading, mutate, isValidating } = useSWR<IDetailResponse<IMerchant>>(
    id ? `/v4/dash/merchants/${id}` : null,
    fetcher
  );

  return { data: data?.data, error, isLoading, mutate, isValidating };
};

const fetchMerchantsByIds = async (ids: Array<number | string>) => {
  const responses = await Promise.all(
    ids.map(id => fetcher(`/v4/dash/merchants/${id}`) as Promise<IDetailResponse<IMerchant>>)
  );

  return responses.map(response => response.data);
};

export const useMerchantsByIds = (ids?: Array<number | string> | null) => {
  const shouldFetch = Boolean(ids?.length);
  const key = shouldFetch ? ['merchantsByIds', ...(ids as Array<number | string>)] : null;
  const { data, error, isLoading, mutate, isValidating } = useSWR<IMerchant[]>(key, () =>
    fetchMerchantsByIds(ids as Array<number | string>)
  );

  return { data, error, isLoading, mutate, isValidating };
};

export const useMerchantList = (params: object) => {
  const { data, error, isLoading, mutate, isValidating } = useSWR<IPaginatedResponse<IMerchant>>(
    `/v4/dash/merchants?${buildQuery(params)}`,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, error, isLoading, mutate, isValidating };
};

export interface IMerchantLocation {
  id: number;
  name: string;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address_line_3?: string | null;
  city?: string | null;
  country?: string | null;
  state?: string | null;
  postal_code?: string | null;
  phone_number?: string | null;
}

export interface IMerchantPin {
  id: number;
  pin: string;
  state: string;
  tag?: string | null;
}

export interface IMerchantUserAccount {
  id: number;
  username: string;
  email: string;
  state?: string;
}

export const useMerchantLocationList = (
  merchantId?: number | string | null,
  params?: Record<string, any>
) => {
  const { data, error, isLoading, mutate, isValidating } = useSWR<
    IPaginatedResponse<IMerchantLocation>
  >(merchantId ? { url: `/v4/dash/merchants/${merchantId}/locations`, params } : null, fetcher);

  return { data: data?.data, meta: data?.meta, error, isLoading, mutate, isValidating };
};

export const useMerchantLocation = (
  merchantId?: number | string | null,
  locationId?: number | string | null
) => {
  const key =
    merchantId && locationId ? `/v4/dash/merchants/${merchantId}/locations/${locationId}` : null;
  const { data, error, isLoading, mutate, isValidating } = useSWR<
    IDetailResponse<IMerchantLocation>
  >(key, fetcher);

  return { data: data?.data, error, isLoading, mutate, isValidating };
};

export const useMerchantPinList = (
  merchantId?: number | string | null,
  params?: Record<string, any>
) => {
  const { data, error, isLoading, mutate, isValidating } = useSWR<IPaginatedResponse<IMerchantPin>>(
    merchantId ? { url: `/v4/dash/merchants/${merchantId}/merchant_pins`, params } : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, error, isLoading, mutate, isValidating };
};

export const useMerchantUserAccountList = (
  merchantId?: number | string | null,
  params?: Record<string, any>
) => {
  const { data, error, isLoading, mutate, isValidating } = useSWR<
    IPaginatedResponse<IMerchantUserAccount>
  >(
    merchantId ? { url: `/v4/dash/merchants/${merchantId}/merchant_user_accounts`, params } : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, error, isLoading, mutate, isValidating };
};
